import React, { useState, useEffect } from 'react';
import { Calendar, User, Mail, Phone, MapPin, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { consultationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Consultation = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    travelers: '',
    dates: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-populate form with logged-in user's data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await consultationsAPI.create(formData);
      toast({
        title: "Consultation Request Received!",
        description: "We'll get back to you within 24 hours. Get ready for an adventure!",
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        destination: '',
        travelers: '',
        dates: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#84bc00] to-[#6a9600] text-black py-24 pt-40 relative overflow-hidden">
        {/* Logo Icon Watermark */}
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_conscious-escape/artifacts/o5c63p3f_customcolor_icon_transparent_background.png" 
            alt="" 
            className="w-[600px] h-auto"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            BOOK YOUR <span className="text-white">FREE CONSULTATION</span>
          </h1>
          <p className="text-xl max-w-3xl mx-auto">
            Let's create your perfect sustainable adventure. No pressure, just possibilities.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-transparent hover:border-[#84bc00] transition-all duration-300">
            <h2 className="text-3xl font-black text-black mb-6">Tell Us About Your Dream Trip</h2>
            
            {/* Logged-in member indicator */}
            {user && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="text-green-600" size={20} />
                <div>
                  <p className="text-sm font-semibold text-green-800">Welcome back, {user.name?.split(' ')[0] || 'Member'}!</p>
                  <p className="text-xs text-green-600">Your contact info has been pre-filled.</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <User size={16} className="text-[#84bc00]" />
                  <span>YOUR NAME *</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="border-2 focus:border-[#84bc00] transition-colors duration-300"
                  placeholder="e.g., Maria Rodriguez"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <Mail size={16} className="text-[#84bc00]" />
                  <span>EMAIL *</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="border-2 focus:border-[#84bc00] transition-colors duration-300"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <Phone size={16} className="text-[#84bc00]" />
                  <span>PHONE</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="border-2 focus:border-[#84bc00] transition-colors duration-300"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Destination */}
              <div>
                <Label htmlFor="destination" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <MapPin size={16} className="text-[#84bc00]" />
                  <span>PREFERRED DESTINATION *</span>
                </Label>
                <Select value={formData.destination} onValueChange={(value) => handleChange('destination', value)}>
                  <SelectTrigger className="border-2 focus:border-[#84bc00] transition-colors duration-300">
                    <SelectValue placeholder="Select a destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="colombia">Colombia</SelectItem>
                    <SelectItem value="mexico">Mexico</SelectItem>
                    <SelectItem value="belize">Belize</SelectItem>
                    <SelectItem value="brazil">Brazil</SelectItem>
                    <SelectItem value="panama">Panama</SelectItem>
                    <SelectItem value="cuba">Cuba</SelectItem>
                    <SelectItem value="costa-rica">Costa Rica</SelectItem>
                    <SelectItem value="jamaica">Jamaica</SelectItem>
                    <SelectItem value="not-sure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Number of Travelers */}
              <div>
                <Label htmlFor="travelers" className="text-sm font-bold text-gray-700 mb-2">NUMBER OF TRAVELERS *</Label>
                <Select value={formData.travelers} onValueChange={(value) => handleChange('travelers', value)}>
                  <SelectTrigger className="border-2 focus:border-[#84bc00] transition-colors duration-300">
                    <SelectValue placeholder="Select number" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Just me (solo)</SelectItem>
                    <SelectItem value="2">2 people</SelectItem>
                    <SelectItem value="3-4">3-4 people</SelectItem>
                    <SelectItem value="5-8">5-8 people</SelectItem>
                    <SelectItem value="8+">8+ people</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Travel Dates */}
              <div>
                <Label htmlFor="dates" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <Calendar size={16} className="text-[#84bc00]" />
                  <span>PREFERRED DATES</span>
                </Label>
                <Input
                  id="dates"
                  type="text"
                  value={formData.dates}
                  onChange={(e) => handleChange('dates', e.target.value)}
                  className="border-2 focus:border-[#84bc00] transition-colors duration-300"
                  placeholder="e.g., July 2025 or Flexible"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-sm font-bold text-gray-700 mb-2 flex items-center space-x-2">
                  <MessageSquare size={16} className="text-[#84bc00]" />
                  <span>TELL US MORE</span>
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="border-2 focus:border-[#84bc00] transition-colors duration-300 min-h-32"
                  placeholder="What are you looking for in your trip? Beach clubs? Adventure? Cultural experiences?"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black text-lg font-bold py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl group"
              >
                <span>{submitting ? 'SENDING...' : 'SEND REQUEST'}</span>
                <Send size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </form>
          </div>

          {/* Right: Info */}
          <div className="space-y-8">
            {/* What to Expect */}
            <div className="bg-gradient-to-br from-black to-gray-900 rounded-2xl shadow-xl p-8 text-white">
              <h3 className="text-2xl font-black mb-6 text-[#84bc00]">WHAT TO EXPECT</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center flex-shrink-0 font-black text-black">1</div>
                  <div>
                    <h4 className="font-bold mb-1">We'll Contact You</h4>
                    <p className="text-sm text-gray-300">Within 24 hours via email or phone (your choice)</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center flex-shrink-0 font-black text-black">2</div>
                  <div>
                    <h4 className="font-bold mb-1">Discuss Your Vision</h4>
                    <p className="text-sm text-gray-300">Tell us about your ideal trip, budget, and preferences</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center flex-shrink-0 font-black text-black">3</div>
                  <div>
                    <h4 className="font-bold mb-1">Receive Custom Itinerary</h4>
                    <p className="text-sm text-gray-300">Get a personalized, eco-certified travel plan</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-[#84bc00] rounded-full flex items-center justify-center flex-shrink-0 font-black text-black">4</div>
                  <div>
                    <h4 className="font-bold mb-1">Book & Adventure!</h4>
                    <p className="text-sm text-gray-300">Confirm your trip and get ready for the experience</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-[#84bc00]">
              <h3 className="text-2xl font-black mb-6 text-black">OR CONTACT US DIRECTLY</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="text-[#84bc00]" size={20} />
                  <span className="font-semibold">hello@exciting.travel</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="text-[#84bc00]" size={20} />
                  <span className="font-semibold">(833) 568-8090</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MessageSquare className="text-[#84bc00]" size={20} />
                  <span className="font-semibold">WhatsApp: (833) 568-8090</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-4">Toll-free • No marketing calls</p>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="bg-[#ffb84d] rounded-2xl shadow-xl p-8 text-black">
              <h3 className="text-xl font-black mb-3">100% FREE. ZERO COMMITMENT.</h3>
              <p className="text-sm font-semibold">
                No strings attached. Just expert guidance and personalized recommendations for your sustainable adventure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Consultation;
